"use client";

// Same-origin proxy pattern, matching account-api.ts. The refresh token itself never
// appears here — it lives only in the HttpOnly `ome_customer_refresh` cookie, which the
// browser attaches automatically (same-origin) and which this code can never read.

import {
  interpretQuoteSubmitResponse,
  type QuoteRequestAnswerInput,
  type QuoteRequestSubmitResultDto,
  type RawApiResult,
  type QuoteSubmitOutcome,
} from "./quote-request-api";

export type LoginOutcome = "Success" | "InvalidCredentials" | "EmailNotConfirmed" | "AccountInactive" | "TooManyAttempts";
export type RefreshOutcome = "Success" | "Invalid";
export type UpdateProfileOutcome = "Success" | "InvalidCountry" | "InvalidPhoneNumber" | "AccountNotFound";
export type ChangePasswordOutcome = "Success" | "InvalidCurrentPassword" | "SameAsCurrentPassword" | "AccountNotFound";

export type CustomerSummary = {
  id: string;
  email: string;
  emailConfirmed: boolean;
  firstName: string;
  lastName: string;
};

export type CustomerProfile = CustomerSummary & {
  phoneCountryCode: string;
  phoneNumber: string;
  countryCode: string;
  countryName: string;
  city: string;
  createdAt: string;
  lastLoginAt: string | null;
};

export type LoginResponse = {
  outcome: LoginOutcome;
  accessToken?: string;
  expiresIn?: number;
  customer?: CustomerSummary;
};

export type RefreshResponse = {
  outcome: RefreshOutcome;
  accessToken?: string;
  expiresIn?: number;
};

export type LoginPayload = {
  email: string;
  password: string;
  languageCode: string;
  rememberMe?: boolean;
};

export type UpdateProfilePayload = {
  firstName: string;
  lastName: string;
  phoneCountryCode: string;
  phoneNumber: string;
  countryCode: string;
  city: string;
};

export type ChangePasswordPayload = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

async function postJson<T>(path: string, body?: unknown, accessToken?: string): Promise<{ ok: boolean; status: number; data: T | null }> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(path, {
    method: "POST",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let data: T | null = null;
  try {
    data = (await response.json()) as T;
  } catch {
    data = null;
  }

  return { ok: response.ok, status: response.status, data };
}

export async function loginCustomer(payload: LoginPayload) {
  return postJson<LoginResponse>("/api/public/account/login", payload);
}

export async function refreshCustomerSession() {
  return postJson<RefreshResponse>("/api/public/account/refresh");
}

export async function logoutCustomer() {
  return postJson<{ message: string }>("/api/public/account/logout");
}

export async function getCustomerMe(accessToken: string): Promise<CustomerProfile | null> {
  try {
    const response = await fetch("/api/public/account/me", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) return null;
    return (await response.json()) as CustomerProfile;
  } catch {
    return null;
  }
}

export async function updateCustomerProfile(accessToken: string, payload: UpdateProfilePayload) {
  const response = await fetch("/api/public/account/profile", {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(payload),
  });

  let data: (CustomerProfile & { outcome?: UpdateProfileOutcome }) | { outcome: UpdateProfileOutcome } | null = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  return { ok: response.ok, status: response.status, data };
}

export async function changeCustomerPassword(accessToken: string, payload: ChangePasswordPayload) {
  const result = await postJson<{ outcome: ChangePasswordOutcome }>("/api/public/account/change-password", payload, accessToken);
  return result;
}

// ---------- Quote Requests (Faz 3 — RFQ submit + "Teklif Taleplerim" list/detail) ----------
// Mirrors backend MultiDomain.Domain.Enums.ProductQuoteType / QuoteRequestStatus exactly.
// These are RAW numeric enums on the wire here (no [JsonConverter] on
// MyQuoteRequestListItemDto/DetailDto — unlike the public product-detail and quote-form
// endpoints, which stringify their enums) — see backend QuoteRequestContracts.cs.
export enum ProductQuoteType {
  Block = 1,
  Slab = 2,
  CustomSize = 3,
}

export enum QuoteRequestStatus {
  Submitted = 1,
  Assigned = 2,
  UnderReview = 3,
  WaitingAdditionalInformation = 4,
  ReadyForQuotation = 5,
  Cancelled = 6,
  Completed = 7,
  Archived = 8,
}

export const QUOTE_REQUEST_STATUS_LABELS: Record<QuoteRequestStatus, string> = {
  [QuoteRequestStatus.Submitted]: "Gönderildi",
  [QuoteRequestStatus.Assigned]: "Atandı",
  [QuoteRequestStatus.UnderReview]: "İnceleniyor",
  [QuoteRequestStatus.WaitingAdditionalInformation]: "Ek Bilgi Bekleniyor",
  [QuoteRequestStatus.ReadyForQuotation]: "Teklife Hazır",
  [QuoteRequestStatus.Cancelled]: "İptal Edildi",
  [QuoteRequestStatus.Completed]: "Tamamlandı",
  [QuoteRequestStatus.Archived]: "Arşivlendi",
};

export type SubmitQuoteRequestCommand = {
  productId: string;
  quoteFormTemplateVersionId: string;
  idempotencyKey?: string;
  answers: QuoteRequestAnswerInput[];
  /** Faz 8 — mandatory; the language the customer chose to communicate in, snapshotted
   * immutably onto the RFQ. The customer's own selection is the canonical source — never
   * silently resolved server-side from a profile preference. */
  communicationLanguageCode: string;
};

export type QuoteRequestAnswerDto = {
  fieldId: string;
  fieldCode: string;
  fieldLabel: string;
  fieldType: string;
  unit?: string;
  displayValue: string;
};

export type QuoteRequestFileDto = {
  id: string;
  answerFieldId?: string;
  fileName: string;
  url: string;
  fileSize: number;
};

export type MyQuoteRequestListItemDto = {
  id: string;
  requestNumber: string;
  productId: string;
  productName: string;
  productImageUrl?: string;
  productQuoteType?: ProductQuoteType | null;
  status: QuoteRequestStatus;
  submittedAt: string;
  lastActivityAt: string;
};

export type MyQuoteRequestDetailDto = MyQuoteRequestListItemDto & {
  productSku?: string;
  categoryName?: string;
  answers: QuoteRequestAnswerDto[];
  files: QuoteRequestFileDto[];
  communicationLanguageCode: string;
  communicationLanguageName: string;
  communicationLanguageNativeName: string;
};

export type PagedResponse<T> = {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

/** Authenticated RFQ submit — same request/response/error shapes as the guest endpoint,
 * just without the guest contact fields and with a Bearer access token attached (the
 * server-side scheme is internally named "CustomerBearer" but the HTTP header is still
 * the standard `Authorization: Bearer <token>` — see CustomerQuoteRequestsController). */
export async function submitQuoteRequest(accessToken: string, cmd: SubmitQuoteRequestCommand): Promise<QuoteSubmitOutcome> {
  const result = await postJson<QuoteRequestSubmitResultDto>("/api/public/account/quote-requests", cmd, accessToken);
  return interpretQuoteSubmitResponse(result as RawApiResult<unknown>);
}

export async function getMyQuoteRequests(
  accessToken: string,
  pageNumber = 1,
  pageSize = 20,
): Promise<RawApiResult<PagedResponse<MyQuoteRequestListItemDto>>> {
  const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(pageSize) });
  const response = await fetch(`/api/public/account/quote-requests?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: PagedResponse<MyQuoteRequestListItemDto> | null = null;
  try {
    data = (await response.json()) as PagedResponse<MyQuoteRequestListItemDto>;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

// ---------- Faz 4: guest-RFQ quote-access register + claim ----------

export type RegisterAndClaimOutcome =
  | "Success"
  | "TokenInvalid"
  | "InvalidCountry"
  | "InvalidPhoneNumber"
  | "EmailAlreadyRegistered"
  | "AccountCreatedButSessionFailed";

export type RegisterAndClaimPayload = {
  token: string;
  firstName: string;
  lastName: string;
  phoneCountryCode: string;
  phoneNumber: string;
  countryCode: string;
  city: string;
  password: string;
  confirmPassword: string;
};

export type RegisterAndClaimResponse = {
  outcome: RegisterAndClaimOutcome;
  accessToken?: string;
  expiresIn?: number;
  claimedCount?: number;
  customer?: CustomerSummary;
};

/** New-account quote-access path — creates the account from an already-verified guest
 * token (never a client-supplied email), then establishes a session and claims every
 * eligible guest RFQ for that email in the same call. */
export async function registerAndClaimQuoteAccess(payload: RegisterAndClaimPayload) {
  return postJson<RegisterAndClaimResponse>("/api/public/account/quote-access/register", payload);
}

export type ClaimQuoteAccessOutcome = "Success" | "TokenInvalid" | "EmailMismatch";

export type ClaimQuoteAccessResponse = {
  outcome: ClaimQuoteAccessOutcome;
  claimedCount?: number;
};

/** Existing-account quote-access path — the caller must already be authenticated (a
 * normal login, never a token-based bypass); the backend grants ownership only if the
 * authenticated session's own email matches the token's verified email. */
export async function claimQuoteAccess(accessToken: string, token: string) {
  return postJson<ClaimQuoteAccessResponse>("/api/public/account/quote-access/claim", { token }, accessToken);
}

export async function getMyQuoteRequestDetail(accessToken: string, id: string): Promise<RawApiResult<MyQuoteRequestDetailDto>> {
  const response = await fetch(`/api/public/account/quote-requests/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: MyQuoteRequestDetailDto | null = null;
  try {
    data = (await response.json()) as MyQuoteRequestDetailDto;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

// ---------- Faz 6: additional information — answered only from this account area (§6/§35) ----------

export enum QuoteAdditionalInformationStatus {
  Pending = 1,
  Answered = 2,
  Expired = 3,
  Cancelled = 4,
}

export type MyAdditionalInformationDto = {
  id: string;
  question: string;
  status: QuoteAdditionalInformationStatus;
  requestedAt: string;
  responseDeadlineAt: string;
  answer?: string;
  answeredAt?: string;
};

export async function getMyAdditionalInformation(accessToken: string, quoteRequestId: string): Promise<RawApiResult<MyAdditionalInformationDto[]>> {
  const response = await fetch(`/api/public/account/quote-requests/${encodeURIComponent(quoteRequestId)}/additional-information`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: MyAdditionalInformationDto[] | null = null;
  try {
    data = (await response.json()) as MyAdditionalInformationDto[];
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

export async function answerAdditionalInformation(accessToken: string, quoteRequestId: string, infoId: string, answer: string): Promise<RawApiResult<MyAdditionalInformationDto>> {
  const response = await fetch(`/api/public/account/quote-requests/${encodeURIComponent(quoteRequestId)}/additional-information/${encodeURIComponent(infoId)}/answer`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ answer }),
  });
  let data: MyAdditionalInformationDto | null = null;
  try {
    data = (await response.json()) as MyAdditionalInformationDto;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

// ---------- Faz 10: representative -> customer AI translation (read-only) ----------
// Mirrors admin-api.ts's QuoteRequestTranslation* types exactly (same backend DTO, same
// numeric wire-format convention) — duplicated here rather than imported, since
// customer-auth-api.ts and admin-api.ts are deliberately separate API surfaces with their own
// auth headers/error handling (see this file's own header convention).

export enum QuoteRequestTranslationOutcome {
  Success = 0,
  NotFound = 1,
  SameLanguage = 2,
  NothingToTranslate = 3,
  AiDisabled = 4,
  FeatureDisabled = 5,
  ConfigurationMissing = 6,
  ApiKeyMissing = 7,
  PartialFailure = 8,
  Failed = 9,
}

export type MyQuoteRequestTranslationItemDto = {
  contentType: number;
  sourceEntityId: string;
  fieldLabel?: string;
  originalText: string;
  translatedText?: string;
  translated: boolean;
  isStale: boolean;
};

export type MyQuoteRequestTranslationStateDto = {
  outcome: QuoteRequestTranslationOutcome;
  sourceLanguageCode: string;
  targetLanguageCode: string;
  sameLanguage: boolean;
  items: MyQuoteRequestTranslationItemDto[];
  errorMessage?: string;
};

/** Cache-only — this customer-facing endpoint never triggers an AI call server-side (there is
 * no corresponding generate endpoint for customers at all — §12/§13: only a representative/
 * admin can spend AI budget). Shows whatever a representative has already translated. */
export async function getMyQuoteRequestTranslation(accessToken: string, quoteRequestId: string): Promise<RawApiResult<MyQuoteRequestTranslationStateDto>> {
  const response = await fetch(`/api/public/account/quote-requests/${encodeURIComponent(quoteRequestId)}/translation`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: MyQuoteRequestTranslationStateDto | null = null;
  try {
    data = (await response.json()) as MyQuoteRequestTranslationStateDto;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

// ---------- Faz 11A: customer's own read-only Quote view ----------
// Mirrors admin-api.ts's AdminQuoteRevisionDto/QuoteLineDto shape (same numeric enum wire
// convention) but as its own narrower, independent type set — see CustomerQuoteContracts.cs
// remarks on why this is not a stripped reuse of the staff DTO.

export enum MyQuoteLineType {
  Product = 1,
  ServiceCost = 2,
}

export enum MyQuoteLineDiscountType {
  None = 1,
  Percentage = 2,
  FixedAmount = 3,
}

export enum MyQuoteStatus {
  Draft = 1,
  PendingApproval = 2,
  CorrectionRequested = 3,
  Approved = 4,
  Sent = 5,
  Accepted = 6,
  Rejected = 7,
  RevisionRequested = 8,
  Expired = 9,
  Cancelled = 10,
}

export type MyQuoteLineDto = {
  /** Faz 11B — correlation key for matching this line's AI-translated Description via
   * MyQuoteRequestTranslationItemDto.sourceEntityId (same pattern as
   * MyAdditionalInformationDto.id). */
  id: string;
  lineType: MyQuoteLineType;
  description: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  discountType: MyQuoteLineDiscountType;
  discountValue: number;
  taxRate: number;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  total: number;
  displayOrder: number;
};

export type MyQuoteRevisionDto = {
  /** Faz 11B — correlation key for matching CustomerNote's AI translation. */
  id: string;
  revisionNumber: number;
  status: MyQuoteStatus;
  currencyCode: string;
  validUntil?: string;
  deliveryTerms?: string;
  paymentTerms?: string;
  customerNote?: string;
  sentAt?: string;
  subtotal: number;
  discountTotal: number;
  netTotal: number;
  taxTotal: number;
  grandTotal: number;
  productLine: MyQuoteLineDto;
  serviceCostLines: MyQuoteLineDto[];
};

export type MyQuoteDto = {
  id: string;
  quoteNumber: string;
  quoteRequestId: string;
  requestNumber: string;
  createdAt: string;
  currentRevision: MyQuoteRevisionDto;
};

/** Null (via 404) when no revision has been sent to this customer yet — not an error; the
 * caller simply shows no "Teklifiniz" section, same as getMyQuoteRequestDetail's other
 * not-yet-available states. IDOR-safe: another customer's Quote 404s identically. */
export async function getMyQuote(accessToken: string, quoteRequestId: string): Promise<RawApiResult<MyQuoteDto>> {
  const response = await fetch(`/api/public/account/quote-requests/${encodeURIComponent(quoteRequestId)}/quote`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: MyQuoteDto | null = null;
  try {
    data = (await response.json()) as MyQuoteDto;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

/** Faz 11B — downloads the customer's own Quote PDF (blob + client-side <a download> trigger,
 * same pattern admin-api.ts's downloadQuotePdf uses). Throws on a non-2xx response (404 for
 * "not yours"/"not sent yet", collapsed identically per the IDOR convention) — callers should
 * catch and show a generic failure message rather than distinguishing the reason. */
export async function downloadMyQuotePdf(accessToken: string, quoteRequestId: string): Promise<void> {
  const response = await fetch(`/api/public/account/quote-requests/${encodeURIComponent(quoteRequestId)}/quote/pdf`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw new Error("PDF indirilemedi.");
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "quote.pdf";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ---------- Loading Plan Approvals (Faz 4) ----------
// Mirrors backend MultiDomain.Domain.Enums.LoadingPlanApprovalStatus and
// MultiDomain.Application.Contracts.CustomerLoadingPlanApprovalDto/CustomerLoadingPlanEquipmentDto
// exactly. Deliberately a distinct, simplified shape from the admin-api.ts LoadingPlanApproval*
// types (Faz 4 §18 — never the same screen/DTO as the admin/manager view).

export enum LoadingPlanApprovalStatus {
  Draft = 1,
  RepresentativeSelected = 2,
  SubmittedForManagerApproval = 3,
  ManagerApproved = 4,
  ManagerRejected = 5,
  SubmittedToCustomer = 6,
  CustomerApproved = 7,
  CustomerRejected = 8,
  CustomerRevisionRequested = 9,
  Cancelled = 10,
  Superseded = 11,
}

export type CustomerLoadingPlanEquipmentDto = {
  transportEquipmentTypeCode: string;
  sequenceNo: number;
  blockNos: string[];
};

export type CustomerLoadingPlanApprovalDto = {
  id: string;
  status: LoadingPlanApprovalStatus;
  requestNumber: string;
  packingListNo: string;
  revisionNumber: number;
  transportModeCode: string;
  totalEquipmentCount: number;
  utilizationPercent: number;
  equipment: CustomerLoadingPlanEquipmentDto[];
  submittedToCustomerAt: string | null;
  customerDecisionAt: string | null;
};

export type CustomerLoadingPlanApprovalListItemDto = {
  id: string;
  status: LoadingPlanApprovalStatus;
  requestNumber: string;
  packingListNo: string;
  revisionNumber: number;
  submittedToCustomerAt: string | null;
};

export async function getMyLoadingPlanApprovals(
  accessToken: string,
  pageNumber = 1,
  pageSize = 20,
): Promise<RawApiResult<PagedResponse<CustomerLoadingPlanApprovalListItemDto>>> {
  const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(pageSize) });
  const response = await fetch(`/api/public/account/loading-plan-approvals?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: PagedResponse<CustomerLoadingPlanApprovalListItemDto> | null = null;
  try {
    data = (await response.json()) as PagedResponse<CustomerLoadingPlanApprovalListItemDto>;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

/** Faz 4 — customer's own list of loading plans awaiting/having received their decision.
 * Only ever plans that reached SubmittedToCustomer or later — IsRevisionStale/Manager-only
 * states are never surfaced here (see CustomerLoadingPlanApprovalsController.GetDetail /
 * ILoadingPlanApprovalService.GetForCustomerAsync). IDOR-safe: another customer's approval id
 * 404s identically to a nonexistent one. */
export async function getMyLoadingPlanApproval(accessToken: string, id: string): Promise<RawApiResult<CustomerLoadingPlanApprovalDto>> {
  const response = await fetch(`/api/public/account/loading-plan-approvals/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: CustomerLoadingPlanApprovalDto | null = null;
  try {
    data = (await response.json()) as CustomerLoadingPlanApprovalDto;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

export async function approveMyLoadingPlan(accessToken: string, id: string) {
  return postJson<{ code?: string }>(`/api/public/account/loading-plan-approvals/${encodeURIComponent(id)}/approve`, undefined, accessToken);
}

export async function rejectMyLoadingPlan(accessToken: string, id: string, reason: string) {
  return postJson<{ code?: string }>(`/api/public/account/loading-plan-approvals/${encodeURIComponent(id)}/reject`, { reason }, accessToken);
}

export async function requestMyLoadingPlanRevision(accessToken: string, id: string, reason: string) {
  return postJson<{ code?: string }>(`/api/public/account/loading-plan-approvals/${encodeURIComponent(id)}/request-revision`, { reason }, accessToken);
}

// ---------- Shipments (Faz 5) ----------
// Mirrors backend MultiDomain.Domain.Enums.ShipmentStatus and
// MultiDomain.Application.Contracts.CustomerShipmentDto/CustomerShipmentListItemDto exactly.
// Read-only — a Shipment has no customer-facing write action (Faz 5 §24/§37).

export enum ShipmentStatus {
  Draft = 1,
  ReadyForLoading = 2,
  LoadingInProgress = 3,
  LoadingCompleted = 4,
  ReadyForDispatch = 5,
  Cancelled = 6,
  Dispatched = 7,
  InTransit = 8,
  Delivered = 9,
  Closed = 10,
  DeliveryFailed = 11,
}

export type CustomerShipmentDocumentDto = {
  id: string;
  documentType: number;
  fileName: string;
  documentNumber: string | null;
  issuedAt: string | null;
  uploadedAt: string;
};

export type CustomerShipmentDto = {
  id: string;
  shipmentNo: string;
  status: ShipmentStatus;
  requestNumber: string;
  packingListNo: string;
  transportModeCode: string;
  equipmentCount: number;
  loadedBlockCount: number;
  totalBlockCount: number;
  readyForDispatchAt: string | null;
  dispatchedAt: string | null;
  estimatedArrivalAt: string | null;
  deliveredAt: string | null;
  documents: CustomerShipmentDocumentDto[];
};

export type CustomerShipmentListItemDto = {
  id: string;
  shipmentNo: string;
  status: ShipmentStatus;
  requestNumber: string;
  packingListNo: string;
  createdAt: string;
};

export async function getMyShipments(
  accessToken: string,
  pageNumber = 1,
  pageSize = 20,
): Promise<RawApiResult<PagedResponse<CustomerShipmentListItemDto>>> {
  const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(pageSize) });
  const response = await fetch(`/api/public/account/shipments?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: PagedResponse<CustomerShipmentListItemDto> | null = null;
  try {
    data = (await response.json()) as PagedResponse<CustomerShipmentListItemDto>;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

export async function getMyShipmentDetail(accessToken: string, id: string): Promise<RawApiResult<CustomerShipmentDto>> {
  const response = await fetch(`/api/public/account/shipments/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: CustomerShipmentDto | null = null;
  try {
    data = (await response.json()) as CustomerShipmentDto;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

/** Faz 6 — downloads one of the customer's own customer-visible Shipment documents (blob + <a
 * download>, same pattern as downloadMyQuotePdf). A wrong-customer/internal-only/nonexistent
 * document all collapse to the same non-2xx response per the IDOR convention. */
export async function downloadMyShipmentDocument(accessToken: string, shipmentId: string, documentId: string, fileName: string): Promise<void> {
  const response = await fetch(`/api/public/account/shipments/${encodeURIComponent(shipmentId)}/documents/${encodeURIComponent(documentId)}/download`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw new Error("Belge indirilemedi.");
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ---------- Return Shipments (Faz 8) ----------
// Mirrors backend MultiDomain.Domain.Enums.ReturnShipmentStatus and
// MultiDomain.Application.Contracts.CustomerReturnShipmentDto exactly. Read-only — a
// ReturnShipment has no customer-facing write action (Faz 8 §39).

export enum ReturnShipmentStatus {
  Draft = 1,
  ReadyForLoading = 2,
  LoadingInProgress = 3,
  LoadingCompleted = 4,
  ReadyForDispatch = 5,
  Dispatched = 6,
  InTransit = 7,
  Returned = 8,
  Closed = 9,
  Cancelled = 10,
}

export type CustomerReturnShipmentDocumentDto = {
  id: string;
  documentType: number;
  fileName: string;
  documentNumber: string | null;
  issuedAt: string | null;
  uploadedAt: string;
};

export type CustomerReturnShipmentDto = {
  id: string;
  returnShipmentNo: string;
  sourceShipmentNo: string;
  status: ReturnShipmentStatus;
  createdAt: string;
  dispatchedAt: string | null;
  estimatedArrivalAt: string | null;
  returnedAt: string | null;
  closedAt: string | null;
  documents: CustomerReturnShipmentDocumentDto[];
};

export async function getMyReturnShipments(
  accessToken: string,
  pageNumber = 1,
  pageSize = 20,
): Promise<RawApiResult<PagedResponse<CustomerReturnShipmentDto>>> {
  const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(pageSize) });
  const response = await fetch(`/api/public/account/return-shipments?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: PagedResponse<CustomerReturnShipmentDto> | null = null;
  try {
    data = (await response.json()) as PagedResponse<CustomerReturnShipmentDto>;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

export async function getMyReturnShipmentDetail(accessToken: string, id: string): Promise<RawApiResult<CustomerReturnShipmentDto>> {
  const response = await fetch(`/api/public/account/return-shipments/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  let data: CustomerReturnShipmentDto | null = null;
  try {
    data = (await response.json()) as CustomerReturnShipmentDto;
  } catch {
    data = null;
  }
  return { ok: response.ok, status: response.status, data };
}

/** Faz 8 — downloads one of the customer's own customer-visible ReturnShipment documents, same
 * pattern as downloadMyShipmentDocument. */
export async function downloadMyReturnShipmentDocument(accessToken: string, returnShipmentId: string, documentId: string, fileName: string): Promise<void> {
  const response = await fetch(`/api/public/account/return-shipments/${encodeURIComponent(returnShipmentId)}/documents/${encodeURIComponent(documentId)}/download`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw new Error("Belge indirilemedi.");
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
